import { useEffect, useRef } from 'react';
import { prefersReducedMotion } from './useInView';

/**
 * Drifts an element against the page scroll, for the full-bleed photography.
 *
 * The element is scaled up so the drift never exposes an edge, and writes are
 * batched into a single rAF frame — transform only, so nothing reflows. Returns
 * a ref for the moving layer; the offset is measured from its parent, which is
 * the frame that stays put.
 */
export default function useParallax({ strength = 0.12, scale = 1.16 } = {}) {
  const ref = useRef(null);

  useEffect(() => {
    const node = ref.current;
    if (!node || prefersReducedMotion()) return undefined;

    const frame = node.parentElement;
    let raf = 0;

    const update = () => {
      raf = 0;
      const rect = frame.getBoundingClientRect();
      const viewport = window.innerHeight;
      if (rect.bottom < 0 || rect.top > viewport) return;

      // -1 when the frame sits below the fold, +1 when it has scrolled past.
      const progress = (viewport / 2 - (rect.top + rect.height / 2)) / viewport;
      const shift = progress * strength * rect.height;
      node.style.transform = `translate3d(0, ${shift.toFixed(2)}px, 0) scale(${scale})`;
    };

    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [strength, scale]);

  return ref;
}
